import React, { useState, useEffect, useMemo } from 'react';
import {
  Users, ShoppingBag, Zap, UserPlus, TrendingUp, Store,
  Download, RefreshCw,
} from 'lucide-react';
import { adminStoresAPI, adminStatsAPI, adminPromoterMetricsAPI } from '../../utils/api';
import { toast } from '../../hooks/use-toast';
import { Toaster } from '../ui/toaster';
import { hoyEnMexico, fechaMenos } from '../../utils/dateFormat';
import StoreRedemptionSummary from './StoreRedemptionSummary';
import RedemptionDetailModal from './RedemptionDetailModal';

const PERIODS = [
  { id: 'today', label: 'Hoy' },
  { id: '7d',    label: '7 días' },
  { id: '30d',   label: '30 días' },
  { id: '3m',    label: '3 meses' },
  { id: '1a',    label: '1 año' },
  { id: 'all',   label: 'Todo' },
  /* Fechas exactas, que es lo que Iván pedía una y otra vez: los presets
     contestan «los últimos 30 días», no «del 3 al 9 de septiembre». */
  { id: 'custom', label: 'Fechas exactas' },
];

/*
  El periodo, en días de México.

  Antes esto salía de `toISOString()`, que da la fecha UTC: después de las 6 de
  la tarde hora de México el botón «Hoy» ya pedía el día siguiente y el tablero
  contestaba en blanco. El backend corta los mismos días con mx_desde/mx_hasta.
*/
function getPeriodRange(id) {
  const end = hoyEnMexico();
  switch (id) {
    case 'today': return { start: end, end };
    case '7d':  return { start: fechaMenos(end, { dias: 6 }), end };
    case '30d': return { start: fechaMenos(end, { dias: 29 }), end };
    case '3m':  return { start: fechaMenos(end, { meses: 3 }), end };
    case '1a':  return { start: fechaMenos(end, { anios: 1 }), end };
    case 'all': return { start: '2020-01-01', end };
    default: return { start: end, end };
  }
}

const campoFecha = {
  height: 32, borderRadius: 9999, padding: '0 14px',
  background: '#fff', border: '1px solid rgba(0,0,0,0.08)',
  fontSize: 12, color: '#0A0A0A', outline: 'none',
};

function downloadCSV(rows, filename) {
  if (!rows?.length) return;
  const keys = Object.keys(rows[0]);
  const csv = [
    keys.join(','),
    ...rows.map(r => keys.map(k => `"${String(r[k] ?? '').replace(/"/g, '""')}"`).join(',')),
  ].join('\n');
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

function fmt(n) {
  if (n == null || n === '') return '—';
  return Number(n).toLocaleString('es-MX');
}

/** Pesos sin decimales: en estas cifras los centavos son ruido. */
function dinero(n) {
  return `$${Number(n || 0).toLocaleString('es-MX', { maximumFractionDigits: 0 })}`;
}

/** Las dos cifras grandes de plataforma, con la cifra en degradado de marca. */
function CifraPlataforma({ titulo, valor, sub, gradiente }) {
  return (
    <div style={{
      background: '#fff',
      border: '1px solid rgba(0,0,0,0.06)',
      borderRadius: 20,
      padding: '20px 22px',
    }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.07em' }}>
        {titulo}
      </div>
      <div
        className="font-display"
        style={{
          fontSize: 40,
          fontWeight: 700,
          letterSpacing: '-0.035em',
          lineHeight: 1.05,
          margin: '10px 0 8px',
          backgroundImage: gradiente,
          WebkitBackgroundClip: 'text',
          backgroundClip: 'text',
          color: 'transparent',
        }}
      >
        {valor}
      </div>
      <div style={{ fontSize: 12.5, color: '#6B7280' }}>{sub}</div>
    </div>
  );
}

function KPICard({ icon: Icon, label, value, sub, accent }) {
  return (
    <div style={{
      background: '#fff',
      border: '1px solid rgba(0,0,0,0.06)',
      borderRadius: 20,
      padding: '18px 20px 16px',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <span style={{ fontSize: 11, fontWeight: 600, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.07em' }}>
          {label}
        </span>
        <div style={{
          width: 30, height: 30, borderRadius: 9,
          background: accent ? '#0A0A0A' : 'rgba(0,0,0,0.05)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          <Icon size={14} color={accent ? '#fff' : '#9CA3AF'} />
        </div>
      </div>
      <div style={{ fontSize: 34, fontWeight: 800, letterSpacing: '-0.03em', color: '#0A0A0A', lineHeight: 1 }}>
        {value}
      </div>
      {sub && (
        <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 7 }}>{sub}</div>
      )}
    </div>
  );
}

function SectionCard({ title, sub, onDownload, downloadDisabled, children }) {
  return (
    <div style={{
      background: '#fff',
      border: '1px solid rgba(0,0,0,0.06)',
      borderRadius: 20,
      padding: '20px 22px',
      marginBottom: 14,
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 18 }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#0A0A0A' }}>{title}</div>
          {sub && <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 3 }}>{sub}</div>}
        </div>
        {onDownload && (
          <button
            onClick={onDownload}
            disabled={downloadDisabled}
            style={{
              display: 'flex', alignItems: 'center', gap: 5,
              height: 30, padding: '0 12px', borderRadius: 9999,
              border: '1px solid rgba(0,0,0,0.08)', background: '#fff',
              fontSize: 11, fontWeight: 500, color: downloadDisabled ? '#D1D5DB' : '#6B7280',
              cursor: downloadDisabled ? 'default' : 'pointer', flexShrink: 0,
            }}
          >
            <Download size={12} /> CSV
          </button>
        )}
      </div>
      {children}
    </div>
  );
}

const Statistics = () => {
  const [period, setPeriod] = useState('30d');
  /* Arranca en la última semana para que, al tocar «Fechas exactas», los campos
     vengan con algo sensato en vez de vacíos. */
  const [rangoManual, setRangoManual] = useState(() => ({
    start: fechaMenos(hoyEnMexico(), { dias: 6 }),
    end: hoyEnMexico(),
  }));
  const [stats, setStats]   = useState(null);
  const [topStores, setTopStores] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [storesCount, setStoresCount] = useState({ active: 0, total: 0 });
  const [dashboardPromoters, setDashboardPromoters] = useState([]);
  const [redemptionSummary, setRedemptionSummary] = useState({ grandTotal: 0, stores: [] });
  const [loading, setLoading] = useState(true);
  const [redemptionLoading, setRedemptionLoading] = useState(false);
  const [redemptionModal, setRedemptionModal] = useState({
    open: false, storeId: null, storeName: '', date: null, start: '', end: '', dayLabel: null,
  });

  const { start, end } = useMemo(
    () => (period === 'custom' ? rangoManual : getPeriodRange(period)),
    [period, rangoManual],
  );

  /* `loadStats` depende del rango: si no, el selector de periodo se movía y las
     cifras de arriba se quedaban quietas — el filtro estaba puesto y no filtraba
     nada. Iván lo pidió como «revisar en rango de fechas» (13-ago-2026). */
  useEffect(() => { loadStats(); }, [start, end]);

  useEffect(() => {
    if (!start || !end) return;
    setRedemptionLoading(true);
    adminStatsAPI.getRedemptionsSummary({ start, end })
      .then(res => setRedemptionSummary({ grandTotal: res.data?.grandTotal ?? 0, stores: res.data?.stores ?? [] }))
      .catch(() => setRedemptionSummary({ grandTotal: 0, stores: [] }))
      .finally(() => setRedemptionLoading(false));
  }, [start, end]);

  const loadStats = async () => {
    setLoading(true);
    try {
      const [overviewRes, topRes, activityRes, storesRes, promotersRes] = await Promise.all([
        adminStatsAPI.getOverview({ start, end }),
        adminStatsAPI.getTopStores({ start, end }),
        adminStatsAPI.getRecentActivity({ start, end }),
        adminStoresAPI.getAll(),
        adminPromoterMetricsAPI.getDashboardPromoters({ date_from: start, date_to: end }),
      ]);
      setStats(overviewRes.data);
      setTopStores(topRes.data.stores || []);
      setRecentActivity(activityRes.data.activities || []);
      const stores = storesRes.data;
      setStoresCount({ active: stores.filter(s => s.isParticipating).length, total: stores.length });
      if (promotersRes?.success && promotersRes?.data?.promoters) {
        setDashboardPromoters(promotersRes.data.promoters);
      }
    } catch (err) {
      console.error(err);
      toast({ title: 'Error', description: 'No se pudieron cargar las estadísticas', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const periodLabel = period === 'custom'
    ? `${start} a ${end}`
    : (PERIODS.find(p => p.id === period)?.label ?? '');
  const activePct   = stats?.totalMembers > 0
    ? Math.round((stats.activeMembers / stats.totalMembers) * 100)
    : 0;

  const downloadRedemptions = () => downloadCSV(
    redemptionSummary.stores.map(s => ({
      Tienda: s.storeName ?? s.name,
      Redenciones: s.total,
      Consumo: s.spend ?? 0,
      Ahorro: s.saved ?? 0,
      'Canjes con monto': s.withAmount ?? 0,
    })),
    `redenciones-${period}-${end}.csv`
  );
  const downloadPromoters = () => downloadCSV(
    dashboardPromoters.map(p => ({
      Canal: p.name, Visitas: p.visits || 0,
      Registros: p.registrations || 0, Pagos: p.payments || 0,
      'Ingresos MXN': p.revenue || 0,
    })),
    `canales-${period}-${end}.csv`
  );
  const downloadActivity = () => downloadCSV(
    recentActivity.map(a => ({ Miembro: a.member, Beneficio: a.benefit, Tienda: a.store, Hora: a.time })),
    `actividad-reciente-${end}.csv`
  );

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '80px 0' }}>
        <span style={{ color: '#9CA3AF', fontSize: 14 }}>Cargando estadísticas...</span>
      </div>
    );
  }

  return (
    <>
      <div style={{ padding: '28px 24px', maxWidth: 1060, margin: '0 auto' }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 18 }}>
          <div>
            <h2 style={{ fontSize: 20, fontWeight: 700, letterSpacing: '-0.02em', color: '#0A0A0A', margin: 0 }}>
              Overview
            </h2>
            <p style={{ fontSize: 12, color: '#9CA3AF', marginTop: 3, marginBottom: 0 }}>
              {start === end ? start : `${start} → ${end}`}
            </p>
          </div>
          <button
            onClick={loadStats}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              height: 32, padding: '0 14px', borderRadius: 9999,
              border: '1px solid rgba(0,0,0,0.08)', background: '#fff',
              fontSize: 12, fontWeight: 500, color: '#6B7280', cursor: 'pointer',
            }}
          >
            <RefreshCw size={12} /> Actualizar
          </button>
        </div>

        {/* Period pills */}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 24 }}>
          {PERIODS.map(p => {
            const active = period === p.id;
            return (
              <button
                key={p.id}
                onClick={() => setPeriod(p.id)}
                style={{
                  height: 32, padding: '0 16px', borderRadius: 9999,
                  background: active ? '#0A0A0A' : '#fff',
                  color: active ? '#fff' : '#6B7280',
                  border: active ? 'none' : '1px solid rgba(0,0,0,0.08)',
                  fontSize: 12, fontWeight: active ? 600 : 400,
                  cursor: 'pointer', transition: 'all .12s ease',
                }}
              >
                {p.label}
              </button>
            );
          })}
        </div>

        {period === 'custom' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: -14, marginBottom: 24 }}>
            <input
              type="date"
              value={rangoManual.start}
              max={rangoManual.end}
              onChange={(e) => setRangoManual((r) => ({ ...r, start: e.target.value }))}
              style={campoFecha}
            />
            <span style={{ fontSize: 12, color: '#6B7280' }}>a</span>
            <input
              type="date"
              value={rangoManual.end}
              min={rangoManual.start}
              max={hoyEnMexico()}
              onChange={(e) => setRangoManual((r) => ({ ...r, end: e.target.value }))}
              style={campoFecha}
            />
          </div>
        )}

        {/* KPI Cards — 3 col */}
        {/*
          El dinero que mueve la plataforma (Iván, 11-ago-2026).

          Va destacado y arriba porque no es un KPI más: son las dos cifras que
          resumen el negocio. Salen de los tickets que capturan los cajeros al
          canjear — no de la venta de membresías.
        */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
          gap: 12, marginBottom: 12,
        }}>
          {/* El rótulo dice el periodo, no «total»: ahora estas dos cifras sí
              cambian con el selector, y llamarlas totales sería mentir en la
              dirección contraria. */}
          <CifraPlataforma
            titulo={`Ahorro de los miembros · ${periodLabel.toLowerCase()}`}
            valor={dinero(stats?.totalSaved)}
            sub={`${dinero(stats?.averageSaving)} por compra`}
            gradiente="linear-gradient(100deg, #F52B16, #FA759F)"
          />
          <CifraPlataforma
            titulo={`Consumo en tiendas · ${periodLabel.toLowerCase()}`}
            valor={dinero(stats?.totalSpend)}
            sub={`ticket promedio ${dinero(stats?.averageTicket)}`}
            gradiente="linear-gradient(100deg, #FA4D0A, #F73E48)"
          />
        </div>

        {/*
          Aviso honesto: los canjes anteriores al 11-ago-2026 no traen monto,
          porque hasta esa fecha el canje solo pedía el número de ticket. Sin
          este contexto, un total bajo parece un mal mes cuando en realidad es
          historial sin capturar.
        */}
        {stats && stats.redemptionsWithAmount < stats.totalRedemptions && (
          <p style={{ fontSize: 12, color: '#9CA3AF', margin: '0 0 20px', lineHeight: 1.5 }}>
            Las cifras de arriba salen de {fmt(stats.redemptionsWithAmount)} de{' '}
            {fmt(stats.totalRedemptions)} canjes. Los demás son anteriores a que
            empezáramos a registrar el monto de cada compra, así que no suman.
          </p>
        )}

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
          gap: 12, marginBottom: 20,
        }}>
          {/* Miembros y activos son fotos del presente y no se acotan al
              periodo: acotarlas contestaría «cuántos entraron» con la etiqueta
              de «cuántos hay». Los que sí entraron en el periodo van aparte. */}
          <KPICard icon={Users}       label="Miembros"       value={fmt(stats?.totalMembers)}           sub="total registrados" />
          <KPICard icon={Zap}         label="Activos"         value={fmt(stats?.activeMembers)}          sub={`${activePct}% del total`}             accent />
          <KPICard icon={UserPlus}    label="Nuevos"          value={fmt(stats?.newMembers)}             sub={`registrados · ${periodLabel.toLowerCase()}`} />
          <KPICard icon={ShoppingBag} label="Redenciones"    value={fmt(redemptionSummary.grandTotal)}   sub={periodLabel} />
          <KPICard icon={Store}       label="Tiendas"        value={fmt(storesCount.active)}             sub={`de ${storesCount.total} registradas`} />
          <KPICard icon={TrendingUp}  label="Canales"        value={fmt(dashboardPromoters.length)}      sub="con actividad" />
          <KPICard icon={UserPlus}    label="Avg / miembro"  value={fmt(stats?.averagePerMember)}        sub="redenciones promedio" />
        </div>

        {/*
          Registros y compras (Iván, 19-sep-2026).

          «En el día de hoy me aparece que hubo cero registrados, pero también
          quiero saber cuántas compras.» Son dos cosas distintas: un día puede
          cerrar sin un solo registro y con ventas, porque quien paga hoy pudo
          registrarse hace semanas. Por eso se enseñan las dos, y el cruce entre
          ellas — que es lo que de verdad dice si el embudo está funcionando.
        */}
        <div style={{
          background: '#fff', borderRadius: 16, border: '1px solid rgba(0,0,0,0.06)',
          padding: '18px 20px', marginBottom: 20,
        }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 14 }}>
            Registros y compras · {periodLabel.toLowerCase()}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 14 }}>
            {[
              ['Se registraron', stats?.newMembers, 'cuentas nuevas'],
              ['Compras', stats?.compras, 'con al menos un pago'],
              ['Se registraron y compraron', stats?.seRegistraronYCompraron, 'el mismo periodo'],
              ['Solo se registraron', stats?.soloSeRegistraron, 'todavía sin pagar'],
              ['Compras de gente anterior', stats?.comprasDeAntiguos, 'se registraron antes'],
            ].map(([rotulo, valor, nota]) => (
              <div key={rotulo}>
                <div style={{ fontSize: 24, fontWeight: 700, color: '#0A0A0A', letterSpacing: '-0.02em' }}>
                  {fmt(valor ?? 0)}
                </div>
                <div style={{ fontSize: 12.5, color: '#374151', marginTop: 2 }}>{rotulo}</div>
                <div style={{ fontSize: 11.5, color: '#9CA3AF' }}>{nota}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Redenciones por tienda */}
        <SectionCard
          title="Redenciones por tienda"
          sub={`${periodLabel} · ${fmt(redemptionSummary.grandTotal)} total`}
          onDownload={downloadRedemptions}
          downloadDisabled={!redemptionSummary.stores.length}
        >
          {redemptionLoading ? (
            <div style={{ padding: '28px 0', textAlign: 'center', color: '#9CA3AF', fontSize: 13 }}>
              Cargando...
            </div>
          ) : redemptionSummary.stores.length === 0 ? (
            <div style={{ padding: '28px 0', textAlign: 'center', color: '#9CA3AF', fontSize: 13 }}>
              Sin redenciones en este período
            </div>
          ) : (
            <StoreRedemptionSummary
              stores={redemptionSummary.stores}
              grandTotal={redemptionSummary.grandTotal}
              onStoreClick={(storeId, storeName) =>
                setRedemptionModal({ open: true, storeId, storeName, date: null, start, end, dayLabel: null })
              }
              onDayClick={(storeId, storeName, date) =>
                setRedemptionModal({
                  open: true, storeId, storeName, date, start: date, end: date,
                  dayLabel: new Date(date + 'T12:00:00').toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric' }),
                })
              }
            />
          )}
        </SectionCard>

        {/* Canales */}
        <SectionCard
          title="Canales de adquisición"
          sub={`Conversión por promotor · ${periodLabel}`}
          onDownload={downloadPromoters}
          downloadDisabled={!dashboardPromoters.length}
        >
          {dashboardPromoters.length === 0 ? (
            <div style={{ padding: '28px 0', textAlign: 'center', color: '#9CA3AF', fontSize: 13 }}>
              Sin datos de canales en este período
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 10 }}>
              {dashboardPromoters.map(p => (
                <div key={p.id} style={{
                  background: 'rgba(0,0,0,0.025)', border: '1px solid rgba(0,0,0,0.06)',
                  borderRadius: 14, padding: '14px 16px',
                }}>
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    fontSize: 13, fontWeight: 700, color: '#0A0A0A', marginBottom: 12,
                  }}>
                    <div style={{
                      width: 26, height: 26, borderRadius: 7, background: '#0A0A0A',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    }}>
                      <UserPlus size={12} color="#fff" />
                    </div>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {p.name}
                    </span>
                  </div>
                  {[
                    { label: 'Visitas',   value: fmt(p.visits || 0) },
                    { label: 'Registros', value: fmt(p.registrations || 0) },
                    { label: 'Pagos',     value: fmt(p.payments || 0) },
                    { label: 'Ingresos',  value: p.revenue > 0 ? `$${Number(p.revenue).toLocaleString('es-MX')}` : '—' },
                  ].map(({ label, value }, i, arr) => (
                    <div key={label} style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      padding: '5px 0',
                      borderBottom: i < arr.length - 1 ? '1px solid rgba(0,0,0,0.04)' : 'none',
                    }}>
                      <span style={{ fontSize: 11, color: '#9CA3AF' }}>{label}</span>
                      <span style={{ fontSize: 12, fontWeight: 600, color: '#0A0A0A' }}>{value}</span>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </SectionCard>

        {/* Tiendas más usadas */}
        {topStores.length > 0 && (
          <SectionCard title="Tiendas más usadas" sub="Por total de redenciones">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {topStores.map((store, i) => (
                <div key={store.id} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{
                    width: 26, height: 26, borderRadius: 7, background: 'rgba(0,0,0,0.06)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    fontSize: 11, fontWeight: 700, color: '#6B7280',
                  }}>
                    {i + 1}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 5 }}>
                      <span style={{ fontSize: 13, fontWeight: 600, color: '#0A0A0A', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {store.name}
                      </span>
                      <span style={{ fontSize: 11, color: '#9CA3AF', flexShrink: 0, marginLeft: 8 }}>
                        {fmt(store.redemptions)}
                      </span>
                    </div>
                    <div style={{ height: 4, background: 'rgba(0,0,0,0.06)', borderRadius: 9999, overflow: 'hidden' }}>
                      <div style={{
                        height: '100%', background: '#0A0A0A', borderRadius: 9999,
                        width: `${Math.min(store.percentage * 5, 100)}%`,
                        transition: 'width .4s ease',
                      }} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </SectionCard>
        )}

        {/* Actividad reciente */}
        {recentActivity.length > 0 && (
          <SectionCard
            title="Actividad reciente"
            sub="Últimas acciones en la plataforma"
            onDownload={downloadActivity}
            downloadDisabled={false}
          >
            <div>
              {recentActivity.map((a, i) => (
                <div key={a.id} style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '9px 0',
                  borderBottom: i < recentActivity.length - 1 ? '1px solid rgba(0,0,0,0.05)' : 'none',
                }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#0A0A0A' }}>{a.member}</div>
                    <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 2 }}>
                      {a.benefit} · {a.store}
                    </div>
                  </div>
                  <span style={{ fontSize: 11, color: '#C0C4CC', flexShrink: 0, marginLeft: 16 }}>
                    {a.time}
                  </span>
                </div>
              ))}
            </div>
          </SectionCard>
        )}

      </div>

      <RedemptionDetailModal
        open={redemptionModal.open}
        onClose={() => setRedemptionModal(m => ({ ...m, open: false }))}
        storeId={redemptionModal.storeId}
        storeName={redemptionModal.storeName}
        date={redemptionModal.date}
        start={redemptionModal.start}
        end={redemptionModal.end}
        dayLabel={redemptionModal.dayLabel}
      />

      <Toaster />
    </>
  );
};

export default Statistics;
